import { FunctionComponent, ReactNode, useState, useEffect } from "react";
import styles from "./SelectionControl.module.css";
import {
  LabelPairedCircleInfoSmRegularIcon,
  LabelPairedCircleInfoMdRegularIcon,
  StandaloneSquareBoldIcon,
  StandaloneSquareCheckFillIcon,
  StandaloneSquareMinusFillIcon,
  StandaloneCircleBoldIcon,
  StandaloneCircleDotBoldIcon,
} from "@deriv/quill-icons";

import { useFocusHandling } from "../../../hooks/useFocusHandling";

const checkIcons = {
  unchecked: StandaloneSquareBoldIcon,
  checked: StandaloneSquareCheckFillIcon,
  indeterminate: StandaloneSquareMinusFillIcon,
};

const radioIcons = {
  unchecked: StandaloneCircleBoldIcon,
  checked: StandaloneCircleDotBoldIcon,
};

const infoIcons = {
  sm: LabelPairedCircleInfoSmRegularIcon,
  md: LabelPairedCircleInfoMdRegularIcon,
};

export type SelectionControlType = {
  className?: string;
  label?: string;
  iconData?: string;
  showInfoIcon?: boolean;

  /** Variant props */
  indicator?: "unchecked" | "checked" | "indeterminate";
  size?: "sm" | "md";
  state?: "default" | "disabled";
  type?: "checkbox" | "radio";

  /** Custom props */
  icon?: ReactNode;
  onChange?: (indicator: "unchecked" | "checked" | "indeterminate") => void;
  getCurrentIndicator?: () => "unchecked" | "checked" | "indeterminate";
};

export const SelectionControl: FunctionComponent<SelectionControlType> = ({
  className = "",
  size = "sm",
  state = "default",
  indicator = "unchecked",
  label = "label",
  showInfoIcon = true,
  type = "checkbox",
  onChange,
}) => {
  const [focus, setFocus] = useState(false);
  const [internalIndicator, setInternalIndicator] = useState(indicator);
  const checkboxId = `quill-selection-control-checkbox-${state}-${size}-${indicator}`;

  useEffect(() => {
    setInternalIndicator(indicator);
    onChange?.(indicator);
  }, [indicator, onChange]);

  const handleClick = () => {
    if (state === "disabled") return;

    const newIndicator =
      internalIndicator === "unchecked" ? "checked" : "unchecked";
    setInternalIndicator(newIndicator);
    onChange?.(newIndicator);
  };

  const CheckIcon = checkIcons[internalIndicator as keyof typeof checkIcons];
  const RadioIcon = radioIcons[internalIndicator as keyof typeof radioIcons];
  const SelectorIcon = type === "checkbox" ? CheckIcon : RadioIcon;
  const InfoIcon = infoIcons[size];

  const { handleFocus, handleBlur } = useFocusHandling({
    isDisabled: state === "disabled",
    setHighlight: setFocus,
    onEnter: () => {
      handleClick();
    },
  });

  return (
    <div
      className={[
        styles.root,
        className,
        "quill-ui-next-component",
        "quill-ui-next-no-select",
        `${state != "disabled" ? "quill-ui-next-pointer" : ""}`,
      ].join(" ")}
      data-id={checkboxId}
      data-indicator={internalIndicator}
      data-size={size}
      data-type={type}
      data-state={state}
      onClick={handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      role="checkbox"
      aria-checked={internalIndicator === "checked"}
      tabIndex={state === "disabled" ? -1 : 0}
    >
      <div className={styles.iconBgFocus}>
        <div className={styles.iconCheckbox}>
          {SelectorIcon && <SelectorIcon iconSize="sm" />}
        </div>
        {focus && <div className={styles.bgFocus} />}
      </div>
      <div className={styles.label1}>
        <div className={styles.label}>{label}</div>
        {showInfoIcon && (
          <div className={styles.iconInfo}>
            <InfoIcon />
          </div>
        )}
      </div>
    </div>
  );
};

export default SelectionControl;
